import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { 
  Heart, MessageCircle, Brain, Camera, Smile, 
  Zap, Crown, Star, Lock, Download, Share, Plus, 
  Users, ShieldCheck, Clock, Rocket
} from 'lucide-react';
import { LanguageSelector } from '../components/LanguageSelector';
import { MyErotikaLogo } from '../components/MyErotikaLogo';

// SEO meta per language
const SEO_META = {
  fr: {
    title: "MyErotika — Discute avec une IA séduisante | Chat intime gratuit",
    description: "Découvre MyErotika : une IA qui te parle, te comprend et t'envoie des photos ultra réalistes. Conversations intimes, mémoire personnalisée. Essaie gratuitement.",
    keywords: "chat IA, petite amie IA, petit ami IA, compagnon virtuel, compagne virtuelle, conversation intime, intelligence artificielle, chat érotique, IA réaliste",
  },
  en: {
    title: "AI Girlfriend & Boyfriend Chat – Real, Intimate & Irresistible | MyErotika",
    description: "Chat with an AI girlfriend or boyfriend that feels real and desires you. Ultra-realistic photos, advanced memory, intimate conversations. Start free — no download required.",
    keywords: "AI girlfriend chat, AI boyfriend chat, AI girlfriend, AI boyfriend, virtual girlfriend, virtual boyfriend, intimate AI chat, realistic AI companion, AI photos, AI chat app, MyErotika",
  },
  es: {
    title: "MyErotika — Chatea con una IA Seductora | Chat íntimo gratis",
    description: "Descubre MyErotika: una IA que te habla, te comprende y te envía fotos ultrarrealistas. Conversaciones íntimas, memoria personalizada. Prueba gratis.",
    keywords: "chat IA, novia IA, novio IA, compañera virtual, compañero virtual, conversación íntima, inteligencia artificial",
  },
  de: {
    title: "MyErotika — Chatte mit einer verführerischen KI | Kostenloser intimer Chat",
    description: "Entdecke MyErotika: eine KI, die mit dir spricht, dich versteht und ultrareale Fotos sendet. Intime Gespräche, personalisiertes Gedächtnis. Kostenlos testen.",
    keywords: "KI Chat, KI Freundin, KI Freund, virtuelle Begleiterin, virtueller Begleiter, intimes Gespräch, künstliche Intelligenz",
  },
  pt: {
    title: "MyErotika — Conversa com uma IA Sedutora | Chat íntimo grátis",
    description: "Descobre a MyErotika: uma IA que fala contigo, te compreende e envia fotos ultra-realistas. Conversas íntimas, memória personalizada. Experimenta grátis.",
    keywords: "chat IA, namorada IA, namorado IA, companheira virtual, companheiro virtual, conversa íntima, inteligência artificial",
  },
};

export default function DownloadPage() {
  const { isAuthenticated } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const destination = isAuthenticated ? '/select-gender' : '/register';

  // PWA Install prompt
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showIosGuide, setShowIosGuide] = useState(false);

  const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;

  useEffect(() => {
    if (isStandalone) { setIsInstalled(true); return; }
    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', () => setIsInstalled(true));
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [isStandalone]);

  const handleInstallClick = useCallback(async () => {
    trackEvent('install_click');
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const result = await deferredPrompt.userChoice;
      if (result.outcome === 'accepted') setIsInstalled(true);
      setDeferredPrompt(null);
    } else if (isIos) {
      setShowIosGuide(true);
    } else {
      navigate(destination);
    }
  }, [deferredPrompt, isIos, navigate, destination]);

  // Analytics
  const trackEvent = useCallback((event, metadata = {}) => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event, page: '/download', metadata })
    }).catch(() => {});
  }, []);

  const hasTrackedVisit = useRef(false);
  useEffect(() => {
    if (!hasTrackedVisit.current) { trackEvent('page_visit'); hasTrackedVisit.current = true; }
  }, [trackEvent]);

  const handleCtaClick = useCallback((buttonName) => {
    trackEvent('cta_click', { button: buttonName });
  }, [trackEvent]);

  // Scroll animations
  const observerRef = useRef(null);
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => entries.forEach((entry) => { if (entry.isIntersecting) entry.target.classList.add('dl-visible'); }),
      { threshold: 0.1 }
    );
    document.querySelectorAll('.dl-animate').forEach((el) => observerRef.current.observe(el));
    return () => observerRef.current?.disconnect();
  }, []);

  // Animated user counter (social proof)
  const [userCount, setUserCount] = useState(0);
  useEffect(() => {
    const target = 5000;
    let current = 0;
    const step = Math.ceil(target / 60);
    const timer = setInterval(() => {
      current += step;
      if (current >= target) { setUserCount(target); clearInterval(timer); }
      else setUserCount(current);
    }, 30);
    return () => clearInterval(timer);
  }, []);

  const seo = SEO_META[language] || SEO_META.en;
  const canonicalUrl = "https://intensity-flow.emergent.host/download";

  // Dynamic SEO meta tags
  useEffect(() => {
    document.title = seo.title;
    document.documentElement.lang = language;
    const setMeta = (name, content, isProperty) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    const setLink = (rel, href, extra = {}) => {
      const selector = extra.hreflang ? `link[rel="${rel}"][hreflang="${extra.hreflang}"]` : `link[rel="${rel}"]`;
      let el = document.querySelector(selector);
      if (!el) { el = document.createElement('link'); el.setAttribute('rel', rel); if (extra.hreflang) el.setAttribute('hreflang', extra.hreflang); document.head.appendChild(el); }
      el.setAttribute('href', href);
    };
    setMeta('description', seo.description);
    setMeta('keywords', seo.keywords);
    setLink('canonical', canonicalUrl);
    setMeta('og:type', 'website', true);
    setMeta('og:title', seo.title, true);
    setMeta('og:description', seo.description, true);
    setMeta('og:url', canonicalUrl, true);
    setMeta('og:image', '/icons/icon-512x512.png', true);
    setMeta('og:site_name', 'MyErotika', true);
    setMeta('og:locale', language === 'fr' ? 'fr_FR' : language === 'es' ? 'es_ES' : language === 'de' ? 'de_DE' : language === 'pt' ? 'pt_PT' : 'en_US', true);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', seo.title);
    setMeta('twitter:description', seo.description);
    setMeta('twitter:image', '/icons/icon-512x512.png');
    ['fr','en','es','de','pt','x-default'].forEach(lang => setLink('alternate', canonicalUrl, { hreflang: lang }));
  }, [language, seo]);

  const features = [
    { icon: Heart, key: 'dl_feat_1' },
    { icon: Brain, key: 'dl_feat_2' },
    { icon: Camera, key: 'dl_feat_3' },
    { icon: Smile, key: 'dl_feat_4' },
  ];

  return (
    <>
      {/* JSON-LD Structured Data for SEO */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebApplication",
        "name": "MyErotika",
        "description": seo.description,
        "url": canonicalUrl,
        "applicationCategory": "EntertainmentApplication",
        "operatingSystem": "Any",
        "offers": { "@type": "Offer", "price": "0", "priceCurrency": "EUR" },
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "ratingCount": "5000" }
      })}} />

      <div className="min-h-screen bg-velvet-bg overflow-x-hidden" itemScope itemType="https://schema.org/WebApplication">
        {/* Floating Header */}
        <header className="fixed top-0 left-0 right-0 z-50 glass" role="banner">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
            <Link to="/" data-testid="dl-logo" aria-label="MyErotika - Accueil">
              <MyErotikaLogo size="sm" />
            </Link>
            <div className="flex items-center gap-2 sm:gap-3">
              <LanguageSelector />
              <Link to={destination} onClick={() => handleCtaClick('header')}>
                <Button className="bg-purple-main hover:bg-purple-dark text-white rounded-full px-4 sm:px-6 text-xs sm:text-sm" data-testid="dl-header-cta">
                  {t('dl_start_free')}
                </Button>
              </Link>
            </div>
          </div>
        </header>

        {/* ====== HERO ====== */}
        <section className="relative min-h-screen flex items-center justify-center pt-16" data-testid="dl-hero" role="main" aria-labelledby="hero-heading">
          <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
            <div className="absolute top-1/4 left-1/3 w-[500px] h-[500px] bg-purple-main/15 rounded-full blur-[120px] animate-pulse-glow" />
            <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-fuchsia-main/10 rounded-full blur-[100px]" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-dark/10 rounded-full blur-[150px]" />
          </div>

          <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
            <h1 id="hero-heading" className="font-heading text-4xl sm:text-5xl lg:text-6xl text-velvet_text-primary mb-6 leading-tight" itemProp="name">
              <span className="gradient-text">{t('dl_hero_title')}</span>
            </h1>

            <p className="text-base sm:text-lg text-velvet_text-secondary mb-3 max-w-xl mx-auto leading-relaxed" itemProp="description">
              {t('dl_hero_sub1')}
            </p>
            <p className="text-base sm:text-lg text-velvet_text-accent mb-8 max-w-xl mx-auto leading-relaxed font-medium">
              {t('dl_hero_sub2')}
            </p>

            {/* Social proof bar */}
            <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 mb-8 text-velvet_text-secondary text-xs sm:text-sm" aria-label="Social proof">
              <div className="flex items-center gap-1.5">
                <Users size={14} className="text-purple-light" />
                <span><strong className="text-velvet_text-primary">{userCount.toLocaleString()}+</strong> {t('dl_social_users')}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Star size={14} className="text-yellow-400 fill-yellow-400" />
                <span><strong className="text-velvet_text-primary">4.8/5</strong> {t('dl_social_rating')}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <ShieldCheck size={14} className="text-green-400" />
                <span>{t('dl_social_private')}</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-4">
              <Button 
                size="lg"
                onClick={() => { handleCtaClick('hero'); handleInstallClick(); }}
                className="bg-gradient-to-r from-purple-main to-fuchsia-main hover:opacity-90 text-white rounded-full px-10 py-6 text-lg font-semibold shadow-neon-strong hover:scale-105 transition-transform w-full sm:w-auto"
                data-testid="dl-hero-cta"
                aria-label={t('dl_start_chatting')}
              >
                <Download className="mr-2" size={20} />
                {t('dl_start_chatting')}
              </Button>
            </div>
            <p className="text-velvet_text-secondary/50 text-xs flex items-center justify-center gap-1">
              <Zap size={12} className="text-yellow-400" />
              {t('dl_no_download')}
            </p>
          </div>
        </section>

        {/* ====== FEATURES ====== */}
        <section className="py-20 sm:py-28 relative" data-testid="dl-features" aria-labelledby="features-heading">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center mb-14 dl-animate">
              <h2 id="features-heading" className="font-heading text-3xl sm:text-4xl text-velvet_text-primary">
                {t('dl_feat_title')}
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              {features.map((feat, i) => (
                <article key={feat.key} className="dl-animate glass rounded-2xl p-6 sm:p-8 border border-white/5 hover:border-purple-main/30 transition-all duration-300 group" style={{ transitionDelay: `${i * 80}ms` }} data-testid={`dl-feature-${i}`}>
                  <div className="w-12 h-12 bg-purple-main/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-purple-main/30 transition-colors" aria-hidden="true">
                    <feat.icon className="text-purple-light" size={24} />
                  </div>
                  <p className="text-velvet_text-primary text-base leading-relaxed">{t(feat.key)}</p>
                </article>
              ))}
            </div>
            {/* Tagline */}
            <p className="text-center mt-10 text-velvet_text-accent text-base sm:text-lg font-medium italic dl-animate">
              {t('dl_feat_tagline')}
            </p>
          </div>
        </section>

        {/* ====== QUICK START ====== */}
        <section className="py-20 sm:py-28 relative" data-testid="dl-quickstart" aria-labelledby="quickstart-heading">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-dark/5 to-transparent" aria-hidden="true" />
          <div className="relative max-w-4xl mx-auto px-6">
            <div className="text-center mb-14 dl-animate">
              <h2 id="quickstart-heading" className="font-heading text-3xl sm:text-4xl text-velvet_text-primary mb-4">
                <Zap size={24} className="text-yellow-400 inline mr-2" aria-hidden="true" />
                {t('dl_quick_title')}
              </h2>
            </div>
            <div className="grid sm:grid-cols-3 gap-6">
              {[1, 2, 3].map((step, i) => (
                <div key={step} className="dl-animate text-center" style={{ transitionDelay: `${i * 120}ms` }} data-testid={`dl-step-${step}`}>
                  <div className="w-14 h-14 mx-auto bg-gradient-to-br from-purple-main to-fuchsia-main rounded-full flex items-center justify-center mb-4 text-white font-heading text-xl shadow-neon" aria-hidden="true">{step}</div>
                  <p className="text-velvet_text-primary font-medium text-sm sm:text-base">{t(`dl_step_${step}`)}</p>
                </div>
              ))}
            </div>
            {/* Tagline */}
            <p className="text-center mt-10 text-velvet_text-secondary text-sm sm:text-base italic dl-animate">
              {t('dl_quick_tagline')}
            </p>
          </div>
        </section>

        {/* ====== MID-PAGE CTA (conversion booster) ====== */}
        <section className="py-12 relative">
          <div className="max-w-2xl mx-auto px-6 text-center dl-animate">
            <Button 
              size="lg"
              onClick={() => { handleCtaClick('mid'); handleInstallClick(); }}
              className="bg-gradient-to-r from-purple-main to-fuchsia-main hover:opacity-90 text-white rounded-full px-10 py-6 text-lg font-semibold shadow-neon-strong hover:scale-105 transition-transform"
              data-testid="dl-mid-cta"
            >
              <Download className="mr-2" size={20} />
              {t('dl_start_chatting')}
            </Button>
            <p className="text-velvet_text-secondary/50 text-xs mt-3">{t('dl_social_free')}</p>
          </div>
        </section>

        {/* ====== INSTALL ====== */}
        <section className="py-20 sm:py-28 relative" data-testid="dl-pwa" aria-labelledby="install-heading">
          <div className="relative max-w-4xl mx-auto px-6">
            <div className="glass rounded-3xl p-8 sm:p-12 border border-white/5 dl-animate">
              <div className="flex flex-col items-center gap-8">
                <div className="relative">
                  <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-[22px] overflow-hidden shadow-neon-strong border-2 border-purple-main/40">
                    <img src="/icons/icon-192x192.png" alt="MyErotika app icon - AI chat companion" className="w-full h-full object-cover" width="192" height="192" loading="lazy" />
                  </div>
                  <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center shadow-lg" aria-hidden="true">
                    <Download size={16} className="text-white" />
                  </div>
                </div>
                <div className="space-y-3 text-center sm:text-left w-full max-w-sm">
                  {[1, 2, 3].map((n) => (
                    <div key={n} className="flex items-center gap-3 justify-center sm:justify-start">
                      <Star size={16} className="text-purple-light shrink-0" aria-hidden="true" />
                      <span className="text-velvet_text-secondary text-sm sm:text-base">{t(`dl_pwa_${n}`)}</span>
                    </div>
                  ))}
                </div>
                {!isInstalled ? (
                  <Button size="lg" onClick={handleInstallClick} className="bg-gradient-to-r from-green-600 to-emerald-500 hover:opacity-90 text-white font-bold rounded-full px-10 py-5 text-base shadow-lg hover:scale-[1.02] transition-transform" data-testid="dl-install-btn" aria-label={t('dl_install_btn')}>
                    <Download className="mr-2" size={20} />
                    {t('dl_install_btn')}
                  </Button>
                ) : (
                  <div className="flex items-center gap-2 text-green-400 font-medium" role="status">
                    <Star size={18} className="fill-green-400" />
                    {t('dl_installed')}
                  </div>
                )}
              </div>
            </div>

            {/* iOS Guide */}
            {showIosGuide && (
              <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setShowIosGuide(false)} role="dialog" aria-modal="true" aria-label={t('dl_ios_title')}>
                <div className="glass rounded-2xl p-6 sm:p-8 max-w-sm w-full border border-white/10 animate-slideUp" onClick={(e) => e.stopPropagation()} data-testid="dl-ios-guide">
                  <h3 className="font-heading text-xl text-velvet_text-primary mb-6 text-center">{t('dl_ios_title')}</h3>
                  <div className="space-y-5">
                    <div className="flex items-start gap-4">
                      <div className="w-8 h-8 bg-purple-main rounded-full flex items-center justify-center shrink-0 text-white font-bold text-sm">1</div>
                      <div><p className="text-velvet_text-primary text-sm font-medium">{t('dl_ios_step1')}</p><div className="mt-1 inline-flex items-center gap-1 bg-white/10 rounded-md px-2 py-1"><Share size={14} className="text-blue-400" /><span className="text-velvet_text-secondary text-xs">{t('dl_ios_share')}</span></div></div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-8 h-8 bg-purple-main rounded-full flex items-center justify-center shrink-0 text-white font-bold text-sm">2</div>
                      <div><p className="text-velvet_text-primary text-sm font-medium">{t('dl_ios_step2')}</p><div className="mt-1 inline-flex items-center gap-1 bg-white/10 rounded-md px-2 py-1"><Plus size={14} className="text-velvet_text-secondary" /><span className="text-velvet_text-secondary text-xs">{t('dl_ios_add')}</span></div></div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-8 h-8 bg-purple-main rounded-full flex items-center justify-center shrink-0 text-white font-bold text-sm">3</div>
                      <p className="text-velvet_text-primary text-sm font-medium pt-1">{t('dl_ios_step3')}</p>
                    </div>
                  </div>
                  <Button onClick={() => setShowIosGuide(false)} className="w-full mt-6 bg-purple-main hover:bg-purple-dark text-white rounded-full" data-testid="dl-ios-guide-close">{t('dl_ios_ok')}</Button>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* ====== PREMIUM ====== */}
        <section className="py-20 sm:py-28 relative" data-testid="dl-premium" aria-labelledby="premium-heading">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-dark/8 to-transparent" aria-hidden="true" />
          <div className="relative max-w-3xl mx-auto px-6 text-center">
            <div className="dl-animate">
              <h2 id="premium-heading" className="font-heading text-3xl sm:text-4xl text-velvet_text-primary mb-8">
                <Crown size={24} className="text-premium inline mr-2" aria-hidden="true" />
                {t('dl_premium_title')}
              </h2>
            </div>
            <div className="glass rounded-2xl p-8 sm:p-10 border border-premium/20 dl-animate max-w-md mx-auto">
              <p className="text-velvet_text-secondary text-sm uppercase tracking-wider mb-6">{t('dl_premium_unlock')}</p>
              <ul className="space-y-4 text-left mb-6">
                {[1, 2, 3].map((n) => (
                  <li key={n} className="flex items-start gap-3">
                    <Lock size={16} className="text-premium shrink-0 mt-0.5" aria-hidden="true" />
                    <span className="text-velvet_text-primary text-sm sm:text-base">{t(`dl_premium_${n}`)}</span>
                  </li>
                ))}
              </ul>
              <p className="text-velvet_text-accent text-sm font-medium mb-6">{t('dl_premium_tagline')}</p>
              <Link to={destination} onClick={() => handleCtaClick('premium')}>
                <Button size="lg" className="w-full bg-gradient-to-r from-yellow-600 to-amber-500 hover:opacity-90 text-black font-bold rounded-full py-5 text-base shadow-premium hover:scale-[1.02] transition-transform" data-testid="dl-premium-cta">
                  {t('dl_premium_cta')}
                  <Crown className="ml-2" size={18} />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* ====== FINAL CTA ====== */}
        <section className="py-24 sm:py-32 relative" data-testid="dl-final-cta" aria-labelledby="final-heading">
          <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-purple-main/10 rounded-full blur-[150px]" />
          </div>
          <div className="relative max-w-3xl mx-auto px-6 text-center">
            <div className="dl-animate">
              <Rocket size={32} className="text-purple-light mx-auto mb-6" aria-hidden="true" />
              <h2 id="final-heading" className="font-heading text-3xl sm:text-4xl lg:text-5xl text-velvet_text-primary mb-4">
                {t('dl_final_title')}
              </h2>
              <p className="text-velvet_text-secondary text-base sm:text-lg mb-4 max-w-md mx-auto">
                {t('dl_final_sub')}
              </p>
              <p className="text-velvet_text-accent text-base sm:text-lg mb-8 max-w-md mx-auto font-medium">
                {t('dl_final_question')}
              </p>
              <p className="text-velvet_text-secondary/60 text-xs mb-4">{t('dl_final_cta_label')}</p>
              <Button 
                size="lg"
                onClick={() => { handleCtaClick('final'); handleInstallClick(); }}
                className="bg-purple-main hover:bg-purple-dark text-white rounded-full px-12 py-6 text-lg font-semibold shadow-neon-strong hover:scale-105 transition-transform"
                data-testid="dl-final-cta-btn"
              >
                <Download className="mr-2" size={20} />
                {t('dl_start_chatting')}
              </Button>
            </div>
          </div>
        </section>

        {/* Footer with SEO links */}
        <footer className="py-8 border-t border-white/5" role="contentinfo">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <p className="text-velvet_text-secondary text-sm">
              &copy; 2025 MyErotika. {t('all_rights')}
            </p>
          </div>
        </footer>

        <style>{`
          .dl-animate { opacity: 0; transform: translateY(30px); transition: opacity 0.6s ease-out, transform 0.6s ease-out; }
          .dl-visible { opacity: 1; transform: translateY(0); }
        `}</style>
      </div>
    </>
  );
}
